import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Form,
  Input,
  Button,
  Spin,
  Row,
  Col,
  Modal,
  message,
} from 'antd';
import {
  SearchOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { onError } from 'utils/handlers';
import FormSubmitControls from 'components/FormSubmitCustomControls';
import Table from 'components/Table';
import Select from 'components/Select';
import { getMetodosDePago, getTiposDeCuentasBancarias } from 'api/configuracion';
import { getBancos } from 'api/configuracion/bancos';
import { getCuentasDeBanco } from 'api/configuracion/cuentasDeBancos';

const BusquedaAvanzadaCuentaBancaria = ({
  visible,
  setVisible,
  callback,
  dataExterno,
  setDataExterno,
  permission,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [metodosDePago, setMetodosDePago] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [cuentasBancarias, setCuentasBancarias] = useState([]);

  useEffect(() => {
    let mounted = true;
    const fetch = async () => {
      setLoading(true);
      const _metodosDePago = await getMetodosDePago();
      const _bancos = await getBancos();
      const _tiposDeCuentasBancarias = await getTiposDeCuentasBancarias();

      if (mounted) {
        setBancos(_bancos.data || []);
        setMetodosDePago(_metodosDePago || []);
        setCuentasBancarias(_tiposDeCuentasBancarias || []);
      }
      setLoading(false);
    };
    fetch();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setVisible(false);
    setData([]);
    setDataExterno([]);
    setSelectedRowKeys([]);
    form.resetFields();
  };

  const handleSearch = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      setSelectedRowKeys([]);
      const params = form.getFieldsValue();
      const keys = Object.keys(params);
      const validProps = keys.filter((prop) => params[prop]);
      if (validProps.length) {
        const result = await getCuentasDeBanco(params);
        if (result?.length) {
          setData(result);
        } else {
          message.warn('No se encontraron Cuentas bancarias');
          setData([]);
        }
      } else {
        message.warn('Debe ingresar al menos un campo');
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onFinishSearch = async (record) => {
    callback(record);
    onCancel();
  };

  const handleLocalSearch = () => {
    const values = form.getFieldsValue();
    const keys = Object.keys(values);
    const validProps = keys.filter((prop) => values[prop]);
    if (validProps.length) {
      const normalizeText = (text = '') => text?.toLowerCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '');

      const validateIncludes = (obj, prop) => {
        if (validProps.includes(prop)) {
          const normalizedValue = normalizeText(obj[prop]);
          const normalizedInput = normalizeText(values[prop]);
          return normalizedValue.includes(normalizedInput);
        }
        return false;
      };

      const validateEquals = (obj, prop) => obj[prop] === values[prop];
      const filtered = data.filter((item) => validateIncludes(item, 'numero_de_cuenta')
        || validateEquals(item, 'banco') || validateEquals(item, 'metodo_de_pago'));
      setData(filtered);
    } else {
      message.warn('Debe ingresar al menos un campo');
    }
  };

  useEffect(() => {
    setData(dataExterno);
    // eslint-disable-next-line
  }, [dataExterno]);

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const add = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      const match = data.find((e) => e.id === key);
      match.metodo_de_pago = metodosDePago.find((e) => e.id === match.metodo_de_pago)?.concepto;
      onFinishSearch(match);
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const controls = [
    {
      text: 'Regresar',
      icon: <ArrowLeftOutlined />,
      onClick: () => onCancel(),
    },
    {
      text: 'Limpiar',
      icon: <DeleteOutlined />,
      onClick: () => {
        setData([]);
        form.resetFields();
      },
    },
  ];

  const columns = [
    {
      titleText: 'Sucursal',
      dataIndex: 'sucursal',
      key: 'sucursal',
      width: 300,
    },
    {
      titleText: 'Número de Cuenta',
      dataIndex: 'numero_de_cuenta',
      key: 'numero_de_cuenta',
      width: 300,
    },
    {
      titleText: 'Banco',
      dataIndex: 'banco',
      key: 'banco',
      width: 220,
      catalog: {
        cat: bancos,
        labelProp: 'nombre',
        use: true,
      },
    },
    {
      titleText: 'Método de Pago',
      dataIndex: 'metodo_de_pago',
      key: 'metodo_de_pago',
      width: 300,
      catalog: {
        cat: metodosDePago,
        labelProp: 'concepto',
        use: true,
      },
    },
  ];

  return (
    <>
      <Modal
        width="90%"
        visible={visible}
        closable={false}
        footer={null}
        title={(
          <Row justify="space-between">
            Búsqueda Avanzada
            <FormSubmitControls
              controls={controls}
              loading={loading}
            />
          </Row>
        )}
        onCancel={onCancel}
        maskClosable={!loading}
        keyboard={!loading}
        forceRender
        centered
      >
        <Spin tip="Cargando..." spinning={loading}>
          <Form
            form={form}
            layout="vertical"
            onFinish={dataExterno?.length ? handleLocalSearch : handleSearch}
          >
            <Row gutter={10}>
              <Col
                xs={24}
                sm={24}
                md={8}
              >
                <Form.Item
                  name="numero_de_cuenta"
                  label="Número de cuenta"
                  hasFeedback
                >
                  <Input allowClear />
                </Form.Item>
              </Col>
              <Col
                xs={24}
                sm={24}
                md={8}
              >
                <Form.Item
                  name="metodo_de_pago"
                  label="Método de pago"
                  hasFeedback
                >
                  <Select dataSource={metodosDePago} labelProp="concepto" />
                </Form.Item>
              </Col>
              <Col
                xs={24}
                sm={24}
                md={8}
              >
                <Form.Item
                  name="tipo_de_cuenta_bancaria"
                  label="Tipo de cuenta bancaria"
                  hasFeedback
                >
                  <Select dataSource={cuentasBancarias} />
                </Form.Item>
              </Col>
              <Col
                xs={24}
                sm={24}
                md={8}
              >
                <Form.Item
                  name="banco"
                  label="Banco"
                  hasFeedback
                >
                  <Select dataSource={bancos} labelProp="nombre" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[10, 5]} justify="end">
              <Col xs={24} sm={24} md={6} lg={4}>
                <Button block htmlType="submit">
                  <SearchOutlined />
                  Buscar
                </Button>
              </Col>
              <Col xs={24} sm={24} md={6} lg={4}>
                <Button
                  onClick={add}
                  block
                  disabled={!selectedRowKeys.length}
                >
                  <PlusOutlined />
                  Agregar
                </Button>
              </Col>
            </Row>
          </Form>
          <Col span={24}>
            <Table
              cols={columns}
              data={data}
              noControls
              allowSort={false}
              allowSearch={false}
              rowSelection={rowSelection}
              onRow={(record) => ({
                onClick: () => {
                  setSelectedRowKeys([record.id]);
                },
                onDoubleClick: add,
              })}
              rowKey="id"
              permission={permission}
            />
          </Col>
        </Spin>
      </Modal>
    </>
  );
};

BusquedaAvanzadaCuentaBancaria.propTypes = {
  callback: PropTypes.func.isRequired,
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  dataExterno: PropTypes.arrayOf(PropTypes.shape({})),
  setDataExterno: PropTypes.func.isRequired,
  permission: PropTypes.objectOf({
    add: PropTypes.bool,
    change: PropTypes.bool,
    delete: PropTypes.bool,
    view: PropTypes.bool,
  }).isRequired,
};

BusquedaAvanzadaCuentaBancaria.defaultProps = {
  dataExterno: [],
};

export default BusquedaAvanzadaCuentaBancaria;
